import React, { useState, useEffect, useCallback } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './NotificationManagement.css';
import { API_BASE_URL } from '../App';
import { adminFetch, getAdminUserSuggestions } from '../services/adminApi';

const MAX_TARGET_USERS = 100;

export const messageTemplates = [
  {
    id: 'android_update',
    name: 'Android Update',
    title: 'YarnGPT update available',
    message: 'A new YarnGPT update is available. Open Google Play to install the latest version.',
    style: 'default',
    color: '#087F5B',
    targetType: 'segment',
    targetGroups: ['android'],
  },
  {
    id: 'ios_update',
    name: 'iOS Update',
    title: 'YarnGPT update available',
    message: 'A new YarnGPT update is available. Open the App Store to install the latest version.',
    style: 'default',
    color: '#087F5B',
    targetType: 'segment',
    targetGroups: ['ios'],
  },
  {
    id: 'new_feature',
    name: 'New Feature',
    title: 'New in YarnGPT',
    message: 'A new YarnGPT feature is ready. Open the app to try it.',
    style: 'default',
    color: '#2563EB',
  },
  {
    id: 'maintenance',
    name: 'Scheduled Maintenance',
    title: 'Scheduled maintenance',
    message: 'YarnGPT will have scheduled maintenance soon. Some services may be unavailable for a short time.',
    style: 'important',
    color: '#B45309',
  },
  {
    id: 'service_restored',
    name: 'Service Restored',
    title: 'YarnGPT service restored',
    message: 'The affected YarnGPT service is working again. Thank you for your patience.',
    style: 'default',
    color: '#087F5B',
  },
  {
    id: 'service_notice',
    name: 'Important Service Notice',
    title: 'Important YarnGPT notice',
    message: 'We have an important service update. Open YarnGPT for the latest information.',
    style: 'important',
    color: '#B91C1C',
  },
  {
    id: 'free_credits',
    name: 'Free Speech Credits',
    title: 'Get free speech credits',
    message: 'Need more speech credits? Tap the gift icon in YarnGPT and watch an ad to earn one.',
    style: 'default',
    color: '#7C3AED',
  },
];

const NotificationManagement = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('send');

  // Form states
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [targetGroups, setTargetGroups] = useState([]);
  const [targetUsers, setTargetUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [userSearchError, setUserSearchError] = useState('');
  const [customData, setCustomData] = useState('');

  // Rich notification styling states
  const [notificationStyle, setNotificationStyle] = useState('default');
  const [customColor, setCustomColor] = useState('#00A86B');
  const [subtitle, setSubtitle] = useState('');
  const [bigText, setBigText] = useState('');
  const [summary, setSummary] = useState('');
  const [actionButtons, setActionButtons] = useState('');
  const [sound, setSound] = useState('default');
  const [badge, setBadge] = useState('1');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeTokens: 0,
    sentToday: 0,
    deliveryRate: 0
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_BASE_URL}/api/admin/notifications/stats`);

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchNotificationLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminFetch(`${API_BASE_URL}/api/admin/notifications/logs`);

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchNotificationLogs();
    }
    fetchStats();
  }, [activeTab, fetchNotificationLogs, fetchStats]);

  useEffect(() => {
    const search = userSearch.trim();
    if (targetType !== 'specific' || search.length < 2) {
      setUserSuggestions([]);
      setSearchingUsers(false);
      setUserSearchError('');
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSearchingUsers(true);
      setUserSearchError('');
      try {
        const response = await getAdminUserSuggestions(search);
        if (!cancelled) setUserSuggestions(response.data.users || []);
      } catch {
        if (!cancelled) {
          setUserSuggestions([]);
          setUserSearchError('Could not load user suggestions.');
        }
      } finally {
        if (!cancelled) setSearchingUsers(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [targetType, userSearch]);

  //upload image to Cloudinary
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    //validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    //validate file size (max 1MB for notification images)
    if (file.size > 1024 * 1024) {
      alert('Image must be less than 1MB for notification images');
      return;
    }

    setUploadingImage(true);
    try {
      //upload to Cloudinary via backend
      const formData = new FormData();
      formData.append('image', file);

      const uploadRes = await adminFetch(`${API_BASE_URL}/api/upload-notification-image`, {
        method: 'POST',
        body: formData
      });

      if (!uploadRes.ok) {
        const errorData = await uploadRes.json().catch(() => ({}));
        console.error('Upload error:', uploadRes.status, errorData);
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const { fileUrl } = await uploadRes.json();
      setImageUrl(fileUrl);
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      alert('Please fill in title and message');
      return;
    }

    if (targetType === 'specific' && targetUsers.length === 0) {
      alert('Select at least one user');
      return;
    }

    setLoading(true);

    try {
      // Build rich notification data
      const notificationData = customData ? JSON.parse(customData) : {};

      // Add styling data
      if (notificationStyle !== 'default') {
        notificationData.style = notificationStyle;
      }
      if (customColor !== '#00A86B') {
        notificationData.color = customColor;
      }
      if (subtitle.trim()) {
        notificationData.subtitle = subtitle.trim();
      }
      if (bigText.trim() && notificationStyle === 'big_text') {
        notificationData.big_text = bigText.trim();
      }
      if (summary.trim()) {
        notificationData.summary = summary.trim();
      }
      if (actionButtons.trim()) {
        notificationData.actions = actionButtons.trim();
      }
      if (sound !== 'default') {
        notificationData.sound = sound;
      }
      if (badge !== '1') {
        notificationData.badge = badge;
      }
      if (imageUrl.trim() && notificationStyle === 'big_picture') {
        notificationData.image_url = imageUrl.trim();
      }

      const payload = {
        title: title.trim(),
        message: message.trim(),
        data: Object.keys(notificationData).length > 0 ? notificationData : {}
      };

      let endpoint = `${API_BASE_URL}/api/admin/notifications/send-all`;

      if (targetType === 'segment') {
        endpoint = `${API_BASE_URL}/api/admin/notifications/send-segment`;
        payload.segments = targetGroups;
      } else if (targetType === 'specific') {
        endpoint = `${API_BASE_URL}/api/admin/notifications/send-targeted`;
        payload.targetUsers = targetUsers.map(user => user.id);
      }

      const response = await adminFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        const skipped = result.totalSkipped || 0;
        const noDeliveryForOneOptedOutUser = targetType === 'specific'
          && payload.targetUsers?.length === 1
          && result.totalTargeted === 0
          && skipped === 1;
        const heading = noDeliveryForOneOptedOutUser
          ? 'No notification sent. The selected user disabled account and service alerts.'
          : result.totalSent > 0
            ? 'Notification sent successfully!'
            : result.message || 'No notification sent.';
        alert(`${heading}\nTargeted: ${result.totalTargeted}\nSent: ${result.totalSent}\nFailed: ${result.totalFailed}\nSkipped by user settings: ${skipped}`);

        // Reset form
        setTitle('');
        setMessage('');
        setTargetGroups([]);
        setTargetUsers([]);
        setUserSearch('');
        setUserSuggestions([]);
        setCustomData('');

        // Reset styling fields
        setNotificationStyle('default');
        setCustomColor('#00A86B');
        setSubtitle('');
        setBigText('');
        setSummary('');
        setActionButtons('');
        setSound('default');
        setBadge('1');

        // Refresh stats
        fetchStats();
      } else {
        alert(`Error: ${result.error || 'Failed to send notification'}`);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Error sending notification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupToggle = (group) => {
    setTargetGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const addTargetUser = (user) => {
    if (targetUsers.some(selectedUser => selectedUser.id === user.id)) return;
    if (targetUsers.length >= MAX_TARGET_USERS) {
      setUserSearchError(`You can select up to ${MAX_TARGET_USERS} users.`);
      return;
    }
    setTargetUsers(previous => [...previous, user]);
    setUserSearch('');
    setUserSuggestions([]);
    setUserSearchError('');
  };

  const removeTargetUser = (userId) => {
    setTargetUsers(previous => previous.filter(user => user.id !== userId));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Apply message template
  const applyTemplate = (template) => {
    setTitle(template.title);
    setMessage(template.message);
    setNotificationStyle(template.style);
    setCustomColor(template.color);
    setSubtitle(template.subtitle || '');
    setSummary(template.summary || '');
    setBigText(template.bigText || '');
    setActionButtons(template.actionButtons || '');
    setCustomData(template.data || '');

    setTargetType(template.targetType || 'all');
    setTargetGroups(template.targetGroups || []);
    setTargetUsers([]);
    setUserSearch('');
    setUserSuggestions([]);
    setSound('default');
    setBadge('1');
  };

  return (
    <div className="notification-management">
      <div className="notification-header">
        <h2>Notification Management</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Users</h3>
            <p>{stats.totalUsers.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3 className="stat-label-with-info">
              Registered Notification Devices
              <InfoOutlinedIcon
                className="stat-info-icon"
                titleAccess="A device token that Firebase has not rejected. This is not an online-user or unique-user count."
              />
            </h3>
            <p>{stats.activeTokens.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Sent Today</h3>
            <p>{stats.sentToday.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Delivery Rate</h3>
            <p>{stats.deliveryRate.toFixed(1)}%</p>
          </div>
        </div>
      </div>

      <div className="notification-tabs">
        <button
          className={`tab ${activeTab === 'send' ? 'active' : ''}`}
          onClick={() => setActiveTab('send')}
        >
          Send Notification
        </button>
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Notification Logs
        </button>
      </div>

      {activeTab === 'send' && (
        <div className="send-notification-section">
          {/* Message Templates Section */}
          <div className="templates-section">
            <h3>Quick Templates</h3>
            <p>Select a template, review its text, and send it after the related event is ready.</p>
            <div className="templates-grid">
              {messageTemplates.map(template => (
                <button
                  type="button"
                  key={template.id}
                  className="template-card"
                  onClick={() => applyTemplate(template)}
                >
                  <div className="template-header">
                    <span className="template-name">{template.name}</span>
                    <div
                      className="template-color"
                      style={{ backgroundColor: template.color }}
                    ></div>
                  </div>
                  <div className="template-preview">
                    <strong>{template.title}</strong>
                    <p>{template.message.substring(0, 80)}...</p>
                    <span className="template-style">{template.style}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSendNotification} className="notification-form">
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Notification title"
                maxLength={100}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Notification message"
                rows={4}
                maxLength={500}
                required
              />
              <small>{message.length}/500 characters</small>
            </div>

            <div className="form-group">
              <label>Target Audience</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    value="all"
                    checked={targetType === 'all'}
                    onChange={(e) => setTargetType(e.target.value)}
                  />
                  All Users
                </label>
                <label>
                  <input
                    type="radio"
                    value="segment"
                    checked={targetType === 'segment'}
                    onChange={(e) => setTargetType(e.target.value)}
                  />
                  User Segment
                </label>
                <label>
                  <input
                    type="radio"
                    value="specific"
                    checked={targetType === 'specific'}
                    onChange={(e) => setTargetType(e.target.value)}
                  />
                  Specific Users
                </label>
              </div>
            </div>

            {targetType === 'segment' && (
              <>
                <div className="form-group">
                  <label>User Segments</label>
                  <div className="checkbox-group segments-grid">
                    <label className="segment-option">
                      <input
                        type="checkbox"
                        checked={targetGroups.includes('new_users')}
                        onChange={() => handleGroupToggle('new_users')}
                      />
                      <span className="segment-label">🆕 New Users</span>
                      <small>Signed up within 7 days</small>
                    </label>
                    <label className="segment-option">
                      <input
                        type="checkbox"
                        checked={targetGroups.includes('inactive')}
                        onChange={() => handleGroupToggle('inactive')}
                      />
                      <span className="segment-label">😴 Inactive Users</span>
                      <small>No activity for 14+ days</small>
                    </label>
                    <label className="segment-option">
                      <input
                        type="checkbox"
                        checked={targetGroups.includes('heavy_users')}
                        onChange={() => handleGroupToggle('heavy_users')}
                      />
                      <span className="segment-label">🔥 Heavy Users</span>
                      <small>50+ speech generations</small>
                    </label>
                    <label className="segment-option">
                      <input
                        type="checkbox"
                        checked={targetGroups.includes('android')}
                        onChange={() => handleGroupToggle('android')}
                      />
                      <span className="segment-label">🤖 Android Users</span>
                      <small>Android devices only</small>
                    </label>
                    <label className="segment-option">
                      <input
                        type="checkbox"
                        checked={targetGroups.includes('ios')}
                        onChange={() => handleGroupToggle('ios')}
                      />
                      <span className="segment-label">🍎 iOS Users</span>
                      <small>iOS devices only</small>
                    </label>
                  </div>
                </div>
              </>
            )}

            {targetType === 'specific' && (
              <div className="form-group">
                <label htmlFor="targetUserSearch">
                  Specific Users ({targetUsers.length}/{MAX_TARGET_USERS})
                </label>
                {targetUsers.length > 0 && (
                  <div className="selected-user-list" aria-label="Selected users">
                    {targetUsers.map(user => (
                      <span className="selected-user-chip" key={user.id}>
                        <span>{user.email || user.username || user.id}</span>
                        <button
                          type="button"
                          onClick={() => removeTargetUser(user.id)}
                          aria-label={`Remove ${user.email || user.username || user.id}`}
                          title="Remove user"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="user-suggestion-field">
                  <input
                    type="search"
                    id="targetUserSearch"
                    value={userSearch}
                    onChange={(event) => setUserSearch(event.target.value)}
                    placeholder="Type an email or exact user ID"
                    autoComplete="off"
                    disabled={targetUsers.length >= MAX_TARGET_USERS}
                  />
                  {searchingUsers && <small>Searching...</small>}
                  {userSearchError && <small className="field-error">{userSearchError}</small>}
                  {!searchingUsers && userSearch.trim().length >= 2 && userSuggestions.length === 0 && !userSearchError && (
                    <small>No matching users found.</small>
                  )}
                  {userSuggestions.length > 0 && (
                    <div className="user-suggestions" role="listbox" aria-label="User suggestions">
                      {userSuggestions
                        .filter(user => !targetUsers.some(selectedUser => selectedUser.id === user.id))
                        .map(user => (
                          <button
                            type="button"
                            role="option"
                            aria-selected="false"
                            key={user.id}
                            onClick={() => addTargetUser(user)}
                          >
                            <strong>{user.email || 'No email'}</strong>
                            <span>{user.username || user.id}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <small>Select each user from the suggestions.</small>
              </div>
            )}

            {/* Rich Notification Styling Section */}
            <div className="styling-section">
              <h3>🎨 Notification Styling</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="notificationStyle">Style Type</label>
                  <select
                    id="notificationStyle"
                    value={notificationStyle}
                    onChange={(e) => setNotificationStyle(e.target.value)}
                  >
                    <option value="default">Default</option>
                    <option value="big_text">Big Text (Expandable)</option>
                    <option value="big_picture">Big Picture</option>
                    <option value="inbox">Inbox Style</option>
                    <option value="messaging">Messaging Style</option>
                    <option value="urgent">Urgent (Critical)</option>
                    <option value="important">Important</option>
                    <option value="passive">Passive</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="customColor">Brand Color</label>
                  <input
                    type="color"
                    id="customColor"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                  />
                </div>
              </div>

              {notificationStyle === 'big_picture' && (
                <div className="form-group">
                  <label htmlFor="imageUrl">🖼️ Image</label>
                  <div className="image-input-group">
                    <input
                      type="text"
                      id="imageUrl"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      style={{ flex: 1 }}
                    />
                    <span className="input-divider">or</span>
                    <label className="upload-btn" htmlFor="imageUpload">
                      {uploadingImage ? '⏳ Uploading...' : '📤 Upload'}
                    </label>
                    <input
                      type="file"
                      id="imageUpload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      disabled={uploadingImage}
                    />
                  </div>
                  <small>Enter URL or upload an image (max 1MB). Recommended: 1024x512px</small>
                  {imageUrl && (
                    <div className="image-preview">
                      <img src={imageUrl} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
                    </div>
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="subtitle">Subtitle (iOS)</label>
                  <input
                    type="text"
                    id="subtitle"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Optional subtitle"
                    maxLength={100}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summary">Summary Text</label>
                  <input
                    type="text"
                    id="summary"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="Brief summary"
                    maxLength={50}
                  />
                </div>
              </div>

              {notificationStyle === 'big_text' && (
                <div className="form-group">
                  <label htmlFor="bigText">Extended Text</label>
                  <textarea
                    id="bigText"
                    value={bigText}
                    onChange={(e) => setBigText(e.target.value)}
                    placeholder="Extended text that appears when notification is expanded"
                    rows={3}
                    maxLength={1000}
                  />
                  <small>This text appears when user expands the notification</small>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="sound">Sound</label>
                  <select
                    id="sound"
                    value={sound}
                    onChange={(e) => setSound(e.target.value)}
                  >
                    <option value="default">Default</option>
                    <option value="chime">Chime</option>
                    <option value="bell">Bell</option>
                    <option value="alert">Alert</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="badge">Badge Count</label>
                  <input
                    type="number"
                    id="badge"
                    value={badge}
                    onChange={(e) => setBadge(e.target.value)}
                    min="0"
                    max="99"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="actionButtons">Action Buttons (JSON)</label>
                <textarea
                  id="actionButtons"
                  value={actionButtons}
                  onChange={(e) => setActionButtons(e.target.value)}
                  placeholder='[{"id": "view", "title": "View", "color": "#00A86B"}, {"id": "dismiss", "title": "Dismiss"}]'
                  rows={2}
                />
                <small>Define action buttons that appear with the notification</small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="customData">🔧 Advanced Custom Data (JSON, optional)</label>
              <textarea
                id="customData"
                value={customData}
                onChange={(e) => setCustomData(e.target.value)}
                placeholder='{"action": "open_home", "url": "https://example.com", "user_data": {"key": "value"}}'
                rows={3}
              />
              <small>Additional custom data for deep linking and app logic (will be merged with styling data)</small>
            </div>

            <button
              type="submit"
              className="send-button"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Notification'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="logs-section">
          {loading ? (
            <div className="loading">Loading logs...</div>
          ) : (
            <div className="logs-table-container">
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Targeted</th>
                    <th>Sent</th>
                    <th>Failed</th>
                    <th>Success Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-data">No notification logs found</td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.sentAt)}</td>
                        <td>
                          <span className={`type-badge ${log.type}`}>
                            {log.type}
                          </span>
                        </td>
                        <td className="title-cell">{log.title}</td>
                        <td className="message-cell">{log.message}</td>
                        <td>{log.totalTargeted}</td>
                        <td className="success">{log.totalSent}</td>
                        <td className="error">{log.totalFailed}</td>
                        <td>
                          {log.totalTargeted > 0
                            ? ((log.totalSent / log.totalTargeted) * 100).toFixed(1) + '%'
                            : 'N/A'
                          }
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
